import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { DEFAULT_LANGUAGE, type LanguageCode } from '@kairelo/shared';
import { en, type Catalogue, type MessageKey } from './messages/en.js';
import { CATALOGUES, TRANSLATED_LANGUAGES } from './catalogues.js';

/**
 * Interface localisation.
 *
 * Hand-rolled rather than a library: the catalogue is a typed object, lookup is
 * a map read, and interpolation is one regular expression. A dependency here
 * would add weight to the page that holds the plaintext for no capability we
 * need (ADR 0005).
 *
 * Two properties matter more than features:
 *
 *   **Missing keys are compile errors.** Every catalogue is typed against the
 *   English one, so a misspelt or forgotten key fails the build rather than
 *   rendering a blank.
 *
 *   **Fallback is per string, not per language.** An untranslated string falls
 *   back to English on its own, so a partially translated catalogue degrades
 *   into a mixed interface rather than a broken one.
 */

export type Translate = (key: MessageKey, values?: Record<string, string | number>) => string;

interface I18nValue {
  language: LanguageCode;
  t: Translate;
  /** Whether the interface itself is translated into the active language. */
  interfaceTranslated: boolean;
}

const I18nContext = createContext<I18nValue>({
  language: DEFAULT_LANGUAGE,
  t: (key) => en[key],
  interfaceTranslated: true,
});

/** Substitutes `{name}` placeholders. Never concatenate sentence fragments. */
function interpolate(template: string, values?: Record<string, string | number>): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match,
  );
}

export function I18nProvider({
  language = DEFAULT_LANGUAGE,
  children,
}: {
  language?: LanguageCode;
  children: ReactNode;
}) {
  const catalogue: Partial<Catalogue> = CATALOGUES[language] ?? {};
  const interfaceTranslated = TRANSLATED_LANGUAGES.includes(language);

  const t = useCallback<Translate>(
    (key, values) => interpolate(catalogue[key] ?? en[key], values),
    [catalogue],
  );

  const value = useMemo(
    () => ({ language, t, interfaceTranslated }),
    [language, t, interfaceTranslated],
  );

  /*
   * Keep the document's own language in step.
   *
   * The wrapper below marks the subtree, which is what a screen reader reading
   * the content needs. `<html lang>` is a separate claim, and it was stuck at
   * the `en` in index.html — so a Croatian page still described itself as
   * English to anything reading at the document level: assistive technology
   * choosing a voice before it reaches the content, and the browser's own
   * offer-to-translate heuristic among them.
   */
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <I18nContext.Provider value={value}>
      {/* `lang` drives hyphenation, spell-check and screen-reader pronunciation. */}
      <div lang={language} style={{ display: 'contents' }}>
        {children}
      </div>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nValue {
  return useContext(I18nContext);
}

/** The common case: just the translate function. */
export function useT(): Translate {
  return useContext(I18nContext).t;
}
